import type { APIGatewayProxyEventV2WithRequestContext, Context } from 'aws-lambda'

export interface CustomAuthorizerContext {
	user?: {
		userId: string
		email: string
		role: UserRole
		providerId?: string
		/**
		 * Populated only when the active JWT was minted by the dev
		 * impersonation flow: holds the actor (admin) userId who initiated
		 * the swap. Drives the impersonation banner on the frontend.
		 */
		impersonatedBy?: string
	}
}

export type CustomAPIGatewayProxyEventV2 = APIGatewayProxyEventV2WithRequestContext<{
	authorizer: CustomAuthorizerContext
}>

export type ApiHandler = (event: CustomAPIGatewayProxyEventV2, context: Context) => Promise<ApiResponse>

export interface ApiResponse {
	statusCode: number
	body: string
	headers?: Record<string, string>
	/**
	 * API Gateway V2 multi-value Set-Cookie slot. Each entry is a single
	 * `Set-Cookie` header value (e.g., `access_token=abc; HttpOnly; ...`).
	 * Use this instead of `headers['Set-Cookie']` when emitting more than
	 * one cookie; `headers` only supports single-valued strings.
	 */
	cookies?: string[]
}

export type ApiRoutesMap = {
	GET?: Record<string, ApiHandler>
	POST?: Record<string, ApiHandler>
	PUT?: Record<string, ApiHandler>
	PATCH?: Record<string, ApiHandler>
	DELETE?: Record<string, ApiHandler>
	OPTIONS?: Record<string, ApiHandler>
}

export enum UserRole {
	SUPER_ADMIN = 'SUPER_ADMIN',
	ADMIN = 'ADMIN',
	USER = 'USER',
	VIEWER = 'VIEWER',
}

export interface QueryParams {
	page?: number
	limit?: number
	sort?: string
	order?: 'asc' | 'desc'
	filter?: Record<string, string | string[]>
	expand?: string[]
	fields?: string[]
}

export interface QueryConfig {
	ALLOWED_FIELDS: Set<string>
	ALLOWED_FILTERS: Set<string>
	ALLOWED_EXPANDS: Set<string>
	ALLOWED_SORT_FIELDS: Set<string>
	DEFAULT_PAGE: number
	DEFAULT_LIMIT: number
	MAX_LIMIT: number
}

export type CustomHandler<TEvent = CustomAPIGatewayProxyEventV2, TResult = ApiResponse> = (
	event: TEvent,
	context: Context,
) => Promise<TResult>
